// src/components/admin/command_center/tabs/SyncEditorTab.jsx
// Pro Sync Studio v5.2 — NSL Lyrics Intelligence Engine
//
// NEW in v5.2 (Precision Panel Upgrade):
//   • Mini-timeline con handles arrastrables START (verde) y END (naranja)
//   • FIN de la línea N = INICIO de la línea N+1 (cascade automático)
//   • Preview 🎧 desde -3s antes para INICIO y para FIN por separado
//   • Nudge grid completo en ambas filas (−10s … +10s)
//   • Propagación de INICIO a líneas siguientes (checkbox)

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useYouTubePlayer } from '../../../../minigames/useYouTubePlayer';
import CajaDeTranscripcion from '../../transcripcion/CajaDeTranscripcion';
import { hidrataCancion, sincronizaParaGuardar } from './hidrataCancion';
import {
    limitesDeLinea, acomoda, desfaseQueCabe, lineasDesordenadas, r1 as aDecima,
} from './tiemposDeLinea';
import './SyncEditorTab.css';

// ══════════════════════════════════════════════════════════════════════
//  WORD INTELLIGENCE ENGINE
// ══════════════════════════════════════════════════════════════════════

const STOP_WORDS = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
    'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has',
    'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might',
    'shall', 'can', 'it', 'its', 'this', 'that', 'these', 'those', 'i', 'you', 'he',
    'she', 'we', 'they', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'our',
    'their', 'what', 'which', 'who', 'how', 'when', 'where', 'all', 'if', 'so', 'as',
    'up', 'out', 'no', 'not', 'just', 'now', 'then', 'than', 'about', 'into', 'also',
    'doo', 'la', 'na', 'oh', 'ah', 'yeah', 'hey', 'ooh', 'mmm', 'ugh', 're', 'da',
]);

const WORD_LEVELS = {
    basic: new Set([
        'sun', 'day', 'night', 'love', 'life', 'time', 'come', 'comes', 'say', 'says', 'see',
        'feel', 'feels', 'know', 'need', 'want', 'make', 'take', 'give', 'think', 'look',
        'good', 'bad', 'big', 'old', 'new', 'long', 'cold', 'warm', 'light', 'dark',
        'back', 'home', 'face', 'smile', 'smiles', 'hand', 'heart', 'sky', 'star', 'rain',
        'snow', 'wind', 'song', 'word', 'voice', 'sound', 'dream', 'hope', 'true', 'live',
        'run', 'walk', 'sing', 'hold', 'break', 'keep', 'lose', 'play', 'stay', 'right',
        'years', 'since', 'seems', 'little', 'darling', 'here', 'there', 'gone', 'got',
        'left', 'made', 'said', 'told', 'found', 'knew', 'put', 'let', 'sit', 'set',
        'fell', 'felt', 'kept', 'saw', 'hear', 'heard', 'stand', 'turn', 'grow', 'show',
        'move', 'bring', 'came', 'went', 'born', 'call', 'stop', 'open', 'close', 'die',
        'far', 'near', 'soon', 'free', 'real', 'clear', 'sure', 'hard', 'easy', 'ready',
        'small', 'great', 'deep', 'high', 'low', 'fast', 'slow', 'strong', 'bright',
        'blue', 'red', 'white', 'black', 'gold', 'green', 'sweet', 'wild', 'fire', 'earth',
        'rise', 'fall', 'shine', 'flow', 'burn', 'beat', 'dance', 'fly',
    ]),
    intermediate: new Set([
        'winter', 'summer', 'spring', 'autumn', 'season', 'lonely', 'slowly', 'melting',
        'returning', 'follows', 'broken', 'golden', 'silent', 'gentle', 'distant',
        'forever', 'together', 'stranger', 'wonder', 'wander', 'river', 'mountain',
        'shadow', 'memory', 'whisper', 'thunder', 'lightning', 'rainbow', 'mirror',
        'forget', 'believe', 'imagine', 'follow', 'become', 'remain', 'belong', 'waiting',
        'watching', 'drifting', 'falling', 'calling', 'burning', 'turning', 'learning',
        'growing', 'changing', 'leaving', 'finding', 'climbing', 'reaching', 'rising',
        'fading', 'hidden', 'wasted', 'stolen', 'empty', 'hollow', 'borrowed', 'carried',
        'yearning', 'fleeting', 'sacred', 'crystal', 'glowing', 'shining', 'sparkling',
        'gleaming', 'soaring', 'dancing', 'spinning', 'trembling', 'wandering', 'healing',
    ]),
    advanced: new Set([
        'celestial', 'inevitable', 'revelation', 'transcend', 'illuminate', 'solitude',
        'melancholy', 'nostalgia', 'ephemeral', 'serenity', 'desolation', 'redemption',
        'eternity', 'infinity', 'mortality', 'illusion', 'surrender', 'liberation',
        'renaissance', 'awakening', 'reminiscent', 'extraordinary', 'magnificent',
        'overwhelming', 'breathtaking', 'devastating', 'shimmering', 'glistening',
        'iridescent', 'luminescent', 'incandescent', 'resplendent', 'translucent',
        'bittersweet', 'labyrinthine', 'kaleidoscope', 'metaphorical', 'philosophical',
    ]),
};

function classifyWord(word) {
    const w = word.toLowerCase().replace(/[^a-z]/g, '');
    if (!w || w.length <= 1) return null;
    if (STOP_WORDS.has(w)) return null;
    if (WORD_LEVELS.basic.has(w)) return 'basic';
    if (WORD_LEVELS.intermediate.has(w)) return 'intermediate';
    if (WORD_LEVELS.advanced.has(w)) return 'advanced';
    if (w.length <= 3) return 'basic';
    if (w.length <= 6) return 'intermediate';
    if (w.length <= 9) return 'advanced';
    return 'expert';
}

const DIFF_CONFIG = {
    basic: { label: 'Básico', emoji: '🟢', color: '#4ADE80', darkColor: 'rgba(74,222,128,0.18)', maxPerLine: 1, targets: ['basic'] },
    intermediate: { label: 'Intermedio', emoji: '🔵', color: '#60A5FA', darkColor: 'rgba(96,165,250,0.18)', maxPerLine: 2, targets: ['basic', 'intermediate'] },
    advanced: { label: 'Avanzado', emoji: '🟠', color: '#FB923C', darkColor: 'rgba(251,146,60,0.18)', maxPerLine: 3, targets: ['basic', 'intermediate', 'advanced'] },
    expert: { label: 'Experto', emoji: '🔴', color: '#F87171', darkColor: 'rgba(248,113,113,0.18)', maxPerLine: 99, targets: ['basic', 'intermediate', 'advanced', 'expert'] },
};

// ══════════════════════════════════════════════════════════════════════
//  HELPERS & PARSERS
// ══════════════════════════════════════════════════════════════════════

const extractId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : url.trim();
};

const cleanWord = (w) => w.replace(/[^a-zA-Z']/g, '');

const formatTime = (secs) => {
    if (secs === null || secs === undefined || isNaN(secs)) return '--:--.---';
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toFixed(3).padStart(6, '0');
    return `${m}:${s}`;
};

const formatTimeCompact = (secs) => {
    if (secs === null || secs === undefined || isNaN(secs)) return '--:--.0';
    const neg = secs < 0;
    const a = Math.abs(secs);
    const m = Math.floor(a / 60);
    const s = Math.floor(a % 60).toString().padStart(2, '0');
    const d = Math.round((a % 1) * 10) % 10;
    return `${neg ? '-' : ''}${m}:${s}.${d}`;
};

const parseTime = (str) => {
    const s = str.trim().replace(',', '.');
    const mmss = s.match(/^(\d+):(\d{1,2})(?:\.(\d*))?$/);
    if (mmss) {
        const frac = mmss[3] ? parseFloat('0.' + mmss[3]) : 0;
        return +mmss[1] * 60 + +mmss[2] + frac;
    }
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
};

const r1 = (v) => Math.round(v * 10) / 10;

const parseAccessibilityTime = (str) => {
    const minSec = str.match(/^(\d+)\s+minute[s]?,\s*(\d+)\s+second[s]?$/i);
    if (minSec) return +minSec[1] * 60 + +minSec[2];
    const secOnly = str.match(/^(\d+)\s+second[s]?$/i);
    if (secOnly) return +secOnly[1];
    const minOnly = str.match(/^(\d+)\s+minute[s]?$/i);
    if (minOnly) return +minOnly[1] * 60;
    return null;
};

const isStageDirection = (text) => {
    const t = text.trim();
    const stageTerms = /\b(guitar|piano|drum|bass|interlude|intro|outro|instrumental|solo|fade|verse|chorus|bridge|soft|acoustic|electric|orchestral|strings|trumpet|violin|flute|synth|introduction|up-tempo)\b/i;
    return /^\(.*\)$/.test(t) && stageTerms.test(t);
};

const createLineObj = (idx, text, time) => {
    const rawWords = text.split(/\s+/).filter(w => w.length > 0);
    return {
        idx, text, time,
        words: rawWords.map((w, wIdx) => {
            const clean = cleanWord(w);
            return {
                wi: wIdx,
                original: w,
                clean,
                level: classifyWord(clean),
                isBlank: false,
                hasDistractors: false,
                choices: [],
            };
        }),
    };
};

// ══════════════════════════════════════════════════════════════════════
//  v5.2: ENHANCED LINE PRECISION PANEL
//  — Mini-timeline con handles START/END arrastrables
//  — FIN = INICIO de la siguiente línea (cascade)
//  — Preview 🎧 desde -3s para INICIO y FIN por separado
// ══════════════════════════════════════════════════════════════════════

// previewing shape: { idx: number, side: 'start' | 'end' } | null

const PREVIEW_PRE = 3.0;   // seconds before cut to seek
const PREVIEW_POST = 3.0;   // seconds after cut to auto-pause
const PREVIEW_MS = (PREVIEW_PRE + PREVIEW_POST) * 1000;

const NUDGE_STEPS = [
    { label: '−10s', delta: -10, cls: 'neg' },
    { label: '−1s', delta: -1, cls: 'neg' },
    { label: '−0.5', delta: -0.5, cls: 'neg' },
    { label: '−0.1', delta: -0.1, cls: 'neg' },
    { label: '+0.1', delta: 0.1, cls: 'pos' },
    { label: '+0.5', delta: 0.5, cls: 'pos' },
    { label: '+1s', delta: 1, cls: 'pos' },
    { label: '+10s', delta: 10, cls: 'pos' },
];

function LinePrecisionPanel({
    lineIdx,
    line,
    prevLine,
    nextLine,
    lines,
    duracion,
    onAcomoda,       // (idx, tiempo) => {movidas}  — mover empujando a las vecinas
    previewing,         // { idx, side } | null
    onPreviewStart,     // (lineIdx, time, side) => void
    onStopPreview,
    onSetTime,          // (lineIdx, time, propagate) => void  — for START of any line
    playerRef,
}) {
    const [editField, setEditField] = useState(null);   // 'start' | 'end'
    const [editVal, setEditVal] = useState('');
    const [propagate, setPropagate] = useState(false);
    const [toastMsg, setToastMsg] = useState('');

    //  ── CLIC ES ELEGIR; ARRASTRAR ES MOVER (07-10-2026) ──────────────
    //  Rob: «también las reglas de precisión como en vídeo: al dar clic es de
    //  selección, ya sea el principio o el final, activar las flechas».
    //
    //  Es el mismo fallo que ya se arregló en los vídeos y que él describió así:
    //  «si sólo intento darle clic en un punto inexacto se modifica, eso no lo
    //  quiero porque rompe con todo». El `mouseup` del handle escribía SIEMPRE el
    //  tiempo de donde estuviera el ratón —y el ratón, al pulsar el handle, está
    //  en medio de sus píxeles, no en su borde—. O sea que tocarlo lo descuadraba.
    //
    //  Ahora un clic sólo ELIGE, y lo elegido se mueve con las flechas hasta la
    //  décima exacta. Para arrastrar hay que moverse de verdad: tres píxeles, más
    //  de lo que tiembla una mano al pulsar y menos de lo que mueve quien quiere
    //  mover.
    const [elegido, setElegido] = useState(null);     // 'start' | 'end' | null
    const [moviendo, setMoviendo] = useState(null);   // mientras se arrastra

    //  ── VOLVER AL PUNTO ORIGINAL (07-10-2026) ────────────────────────
    //  Rob: «sin querer edité el inicio y era el final, y lamentablemente ese
    //  error me costó mucho tiempo corregirlo y afecté a los demás. ¿Crees que
    //  podamos meter un botón de regresar al punto original para corregir mi
    //  error a un solo clic?».
    //
    //  Una foto de cómo estaba esta línea al abrir el panel. Un error de dedo
    //  aquí no se arregla «poniendo el número de antes», porque para entonces ya
    //  no se sabe cuál era: se han dado seis toques a las flechas y el número de
    //  partida se fue con el primero.
    const [original, setOriginal] = useState({ start: null, end: null });

    //  Y la foto de TODAS, porque mover un corte puede correr a varias para
    //  hacerle sitio: devolver sólo éste dejaría a las otras donde las dejó el
    //  empujón, que es peor que el error.
    const fotoDeTiempos = useRef([]);

    //  Y cuánto se llevó la propagación. Mover el INICIO con «propagar» puesto
    //  empuja a TODAS las de abajo, y eso es lo que Rob llama «afecté a los
    //  demás»: devolver sólo esta línea dejaría al resto de la canción corrido.
    //  Como el empujón es un desplazamiento igual para todas, se deshace
    //  empujando lo mismo para el otro lado.
    const arrastre = useRef(0);

    //  Al abrir el panel —o al cambiar de línea— se toma la foto de nuevo. Lo
    //  de antes ya no vale: era de otra línea.
    useEffect(() => {
        setOriginal({ start: line?.time ?? null, end: nextLine?.time ?? null });
        fotoDeTiempos.current = (lines || []).map((l) => l?.time ?? null);
        arrastre.current = 0;
        empujadas.current = { start: new Set(), end: new Set() };
        setElegido(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineIdx]);

    const trackRef = useRef(null);
    const dragging = useRef(null);
    const toastTimer = useRef(null);

    // ▶ El botón de oír el verso entero.
    const [playingSegment, setPlayingSegment] = useState(false);
    //  Por dónde va mientras suena: sin esto no hay forma de distinguir «está
    //  sonando» de «no hizo nada», que es justo lo que le pasó a Rob.
    const [dondeVa, setDondeVa] = useState(null);
    const relojDelSegmento = useRef(null);

    const startTime = line.time ?? 0;
    const endTime = nextLine?.time ?? null;   // END = next line's START
    const duration = endTime !== null ? endTime - startTime : null;

    // ── Local toast ──────────────────────────────────────────────────
    const flash = useCallback((msg) => {
        clearTimeout(toastTimer.current);
        setToastMsg(msg);
        toastTimer.current = setTimeout(() => setToastMsg(''), 2000);
    }, []);

    // ── Seek ─────────────────────────────────────────────────────────
    //  `callado` para cuando el tiempo vino RECORTADO: el aviso que importa es
    //  el del tope —«hasta aquí, y por esto»— y un «▶ 0:56.7» detrás lo borra.
    //  Se quedó así la primera vez: el tope frenaba bien y no lo decía nunca.
    const seek = useCallback((t, callado = false) => {
        try { playerRef?.current?.seekTo(Math.max(0, t), true); } catch (_) { }
        if (!callado) flash(`▶ ${formatTimeCompact(t)}`);
    }, [playerRef, flash]);

    //  ── UN SOLO CAMINO PARA ESCRIBIR UN TIEMPO ───────────────────────
    //  Las flechas, los botones de ±0.1, el arrastre y la casilla donde se
    //  teclea el tiempo salen todos por aquí. Antes cada uno tenía su propio
    //  `Math.max` con números distintos, y por eso unos respetaban a la vecina y
    //  otros no: se podía arrastrar el principio por encima de la siguiente y
    //  dejarla con duración negativa. Eso no se ve aquí; se ve cuando el alumno
    //  oye la frase y el hueco le sale dos líneas más tarde.
    //
    //  La cuenta vive en `tiemposDeLinea.js`, suelta, para poder medirla.
    //  Las que se han tenido que correr para hacerle sitio a este corte, desde
    //  que se abrió el panel. Se guardan para poder devolverlas con el ↩.
    const empujadas = useRef({ start: new Set(), end: new Set() });

    const ponTiempo = useCallback((field, crudo, propaga) => {
        //  El FIN de esta línea es el PRINCIPIO de la siguiente: en una canción
        //  no hay dos números, hay uno.
        const aQuien = field === 'start' ? lineIdx : lineIdx + 1;
        if (aQuien >= lines.length) return null;

        const base = field === 'start' ? startTime : endTime;
        const propagaDeVerdad = field === 'start' && !!propaga;

        if (propagaDeVerdad) {
            //  Propagar es otra herramienta: corre el resto de la canción el
            //  mismo tanto, a propósito. Ahí no se empuja nada — ya se mueve
            //  todo— y sólo hay que apuntar cuánto, para poder devolverlo.
            const val = aDecima(Math.max(0, crudo));
            if (base !== null) arrastre.current += val - base;
            onSetTime(aQuien, val, true);
            return { val, recortado: false, aplicaA: aQuien };
        }

        //  Y si no, se mueve y se le hace sitio: las de abajo —o las de arriba—
        //  se corren sólo lo justo para que les quepa el mínimo.
        const movidas = onAcomoda(aQuien, crudo);
        if (movidas.length) {
            movidas.forEach((j) => empujadas.current[field].add(j));
            flash(`↔ ${movidas.length} línea${movidas.length > 1 ? 's' : ''} corrida`
                + `${movidas.length > 1 ? 's' : ''} para hacer sitio`);
        }
        return {
            val: aDecima(Math.max(0, crudo)),
            //  `callado` para el salto: el aviso que importa es el del empujón
            //  —mover una línea y que se muevan tres sin decirlo es peor que no
            //  poder moverla— y un «▶ 1:41.6» detrás lo borra.
            recortado: movidas.length > 0,
            aplicaA: aQuien,
        };
    }, [lines, lineIdx, startTime, endTime, onSetTime, onAcomoda, flash]);

    //  ── UN SOLO CLIC PARA DESHACER UN ERROR DE DEDO ──────────────────
    //  Devuelve el campo a como estaba al abrir el panel, y si al moverlo
    //  empujó a las de abajo, las devuelve también.
    //
    //  El orden importa: primero se deshace el empujón y después se coloca esta
    //  línea. Al revés, el empujón se calcularía sobre una línea que ya se ha
    //  movido y arrastraría al resto una segunda vez.
    const deshacer = useCallback((field) => {
        const antes = field === 'start' ? original.start : original.end;
        if (antes === null || antes === undefined) return;

        //  Primero las que se corrieron para hacerle sitio: vuelven a donde
        //  estaban al abrir el panel. Si no, deshacer arreglaría este corte y
        //  dejaría a las otras donde las dejó el empujón.
        const corridas = [...empujadas.current[field]];
        for (const j of corridas) {
            const suTiempo = fotoDeTiempos.current[j];
            if (typeof suTiempo === 'number') onSetTime(j, suTiempo, false);
        }
        empujadas.current[field] = new Set();

        if (field === 'start') {
            if (arrastre.current !== 0 && nextLine && endTime !== null) {
                //  Y si lo que hubo fue propagación —otra herramienta—, se empuja
                //  la siguiente para el otro lado: el empujón fue el mismo para
                //  todas, así que el de vuelta también.
                onSetTime(lineIdx + 1, r1(endTime - arrastre.current), true);
                arrastre.current = 0;
            }
            onSetTime(lineIdx, antes, false);
        } else {
            onSetTime(lineIdx + 1, antes, false);
        }
        seek(antes, true);
        flash(`↩ ${field === 'start' ? 'INICIO' : 'FIN'} devuelto a ${formatTimeCompact(antes)}`
            + (corridas.length ? ` y ${corridas.length} línea(s) a su sitio` : ''));
    }, [original, nextLine, endTime, lineIdx, onSetTime, seek, flash]);

    // ── Nudge ────────────────────────────────────────────────────────
    const nudge = useCallback((field, delta) => {
        const base = field === 'start' ? startTime : endTime;
        if (base === null) return;
        const r = ponTiempo(field, base + delta, propagate);
        if (r) seek(r.val, r.recortado);
    }, [startTime, endTime, ponTiempo, seek, propagate]);

    // ── Commit typed value ───────────────────────────────────────────
    const commitEdit = useCallback((field) => {
        const parsed = parseTime(editVal);
        //  Por el mismo sitio que todo lo demás: tecleando un tiempo tampoco se
        //  puede invadir a la vecina.
        if (parsed !== null) {
            const r = ponTiempo(field, parsed, propagate);
            if (r) seek(r.val, r.recortado);
        }
        setEditField(null);
    }, [editVal, ponTiempo, seek, propagate]);

    //  ── LA REGLA NO SE MUEVE MIENTRAS SE EDITA (07-10-2026) ──────────
    //  Rob: «cuando doy clic a 5 segundos, 1 segundo, 10 segundos, se modifican
    //  como ambos, como si se recorriera. No sé si es un efecto visual o es un
    //  error».
    //
    //  Era un efecto visual, y de los peores: el que hace dudar de lo que ves.
    //  Los extremos de la regla salían del FIN, así que al mover el FIN la regla
    //  entera se reescalaba y el handle verde CAMBIABA DE SITIO en pantalla sin
    //  haber cambiado de tiempo. Medido: el INICIO pasó del 28.7 % al 26 % con su
    //  reloj clavado en 0:03.0.
    //
    //  Ahora la regla se fija al abrir la línea y se queda quieta. Sólo se
    //  ensancha —a saltos, no de forma continua— si un corte se sale de ella, y
    //  entonces se nota que lo que ha cambiado es la regla, no el corte.
    const [ventana, setVentana] = useState({ ini: 0, fin: 10 });

    useEffect(() => {
        const base = line?.time ?? 0;
        const ini = prevLine?.time != null ? Math.max(0, prevLine.time - 2)
            : Math.max(0, base - 10);
        const fin = nextLine?.time != null
            ? nextLine.time + Math.max(4, (nextLine.time - base) * 0.15)
            : base + 30;
        setVentana({ ini, fin: Math.max(ini + 1, fin) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineIdx]);

    //  Si un corte se sale, la regla crece de golpe un cuarto de su ancho. A
    //  saltos a propósito: ensancharla un poquito en cada décima sería volver al
    //  problema de antes con otro nombre.
    useEffect(() => {
        const ancho = ventana.fin - ventana.ini;
        const margen = ancho * 0.06;
        const puntos = [startTime, endTime].filter((t) => typeof t === 'number');
        if (!puntos.length) return;
        const bajo = Math.min(...puntos);
        const alto = Math.max(...puntos);
        if (bajo >= ventana.ini + margen && alto <= ventana.fin - margen) return;
        const paso = ancho * 0.25;
        setVentana((v) => ({
            ini: bajo < v.ini + margen ? Math.max(0, bajo - paso) : v.ini,
            fin: alto > v.fin - margen ? alto + paso : v.fin,
        }));
    }, [startTime, endTime, ventana.ini, ventana.fin]);

    const tlStart = ventana.ini;
    const tlEnd = ventana.fin;

    const tlRange = Math.max(1, tlEnd - tlStart);
    const tlPct = (t) => Math.min(100, Math.max(0, ((t - tlStart) / tlRange) * 100));

    const startPct = tlPct(startTime);
    const endPct = endTime !== null ? tlPct(endTime) : null;

    //  Hasta dónde dejan las vecinas. La misma cuenta que frena el arrastre, para
    //  que lo que se ve y lo que se puede sean lo mismo.
    const topes = limitesDeLinea(lines, lineIdx, duracion);

    // ── Drag ─────────────────────────────────────────────────────────
    const tlPosToTime = (clientX) => {
        if (!trackRef.current) return 0;
        const rect = trackRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return r1(tlStart + pct * tlRange);
    };

    //  Tres píxeles. Menos, y el temblor de pulsar ya cuenta como arrastrar;
    //  más, y quien quiere mover poco tiene que pasarse para empezar.
    const UMBRAL_PX = 3;

    const onHandleDown = (e, field) => {
        e.preventDefault();
        dragging.current = field;
        setElegido(field);

        const xInicial = e.touches ? e.touches[0].clientX : e.clientX;
        let seMovio = false;

        const move = (ev) => {
            const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
            if (!seMovio) {
                if (Math.abs(cx - xInicial) < UMBRAL_PX) return;
                seMovio = true;
                setMoviendo(field);
            }
            ponTiempo(dragging.current, tlPosToTime(cx), false);
        };

        const up = (ev) => {
            const cx = ev.changedTouches ? ev.changedTouches[0].clientX : ev.clientX;
            if (seMovio) {
                //  Al soltar se busca EL VALOR QUE QUEDÓ, no donde cayó el ratón:
                //  si se arrastró más allá del tope, la canción tiene que ir a
                //  donde quedó el corte, no a donde quiso llegar la mano.
                const r = ponTiempo(dragging.current, tlPosToTime(cx), propagate);
                if (r) seek(r.val, r.recortado);
            } else {
                //  No se movió: fue un clic. Se queda elegido —para poder
                //  afinarlo con las flechas— y la canción va a DONDE ESTÁ, no a
                //  donde cayó el ratón. Nada cambia de sitio.
                const donde = field === 'start' ? startTime : endTime;
                if (donde !== null) seek(donde);
            }
            seMovio = false;
            setMoviendo(null);
            dragging.current = null;
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseup', up);
            document.removeEventListener('touchmove', move);
            document.removeEventListener('touchend', up);
        };

        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', up);
        document.addEventListener('touchmove', move, { passive: false });
        document.addEventListener('touchend', up);
    };

    //  ── LAS FLECHAS, SOBRE EL HANDLE ELEGIDO ──────────────────────────
    //  Rob: «activar las flechas». Arrastrar sirve para acercarse; la décima se
    //  busca con el teclado, sin soltar la vista de la canción.
    //
    //  No se escucha nada si el cursor está escribiendo: en este mismo panel hay
    //  una casilla donde se teclea el tiempo a mano, y allí las flechas son
    //  suyas.
    useEffect(() => {
        if (!elegido) return;
        //  Sólo los campos DONDE SE ESCRIBE. Antes esto apartaba cualquier
        //  `<input>`, y la casilla de «Propagar INICIO a siguientes» es uno: al
        //  marcarla el foco se quedaba ahí —y el `preventDefault` del handle
        //  impide que se lo lleve nadie—, así que a partir de ese momento las
        //  flechas no hacían nada. Marcar la casilla es justo lo que se hace
        //  antes de afinar, o sea que se rompía en el peor momento.
        const DONDE_SE_ESCRIBE = new Set([
            'text', 'number', 'search', 'url', 'tel', 'email', 'password', 'date', 'time',
        ]);
        const enUnCampo = (el) => {
            const t = (el?.tagName || '').toLowerCase();
            if (t === 'textarea' || el?.isContentEditable) return true;
            if (t !== 'input') return false;
            //  Un `<input>` sin tipo es de texto.
            return DONDE_SE_ESCRIBE.has((el.type || 'text').toLowerCase());
        };
        const alPulsar = (ev) => {
            if (enUnCampo(ev.target)) return;
            const haciaDonde = ev.key === 'ArrowLeft' || ev.key === 'ArrowDown' ? -1
                : ev.key === 'ArrowRight' || ev.key === 'ArrowUp' ? 1 : 0;
            if (!haciaDonde) return;
            ev.preventDefault();
            //  Una décima a secas, un segundo con Shift, diez con Ctrl: los
            //  mismos tres tamaños que los botones de al lado.
            const paso = ev.ctrlKey || ev.metaKey ? 10 : ev.shiftKey ? 1 : 0.1;
            nudge(elegido, haciaDonde * paso);
        };
        window.addEventListener('keydown', alPulsar);
        return () => window.removeEventListener('keydown', alPulsar);
    }, [elegido, nudge]);

    // ──────────────────────────────────────────────────────────────────
    // ▶ Play segment: reproduce desde startTime hasta endTime exacto
    // ──────────────────────────────────────────────────────────────────
    //  ── OÍR EL SEGMENTO ENTERO, PARA COMPROBAR QUE EMPATA ────────────
    //  Rob: «cuando doy clic a ese, no hace nada y se queda exactamente en el
    //  tiempo actual. Lo que yo quiero es un botón donde yo ponga play y diga:
    //  ah, sí, ya empató perfectamente. O no, necesito hacer más
    //  modificaciones. Pero escucharlo y empatarlo con el vídeo, la voz y los
    //  tiempos».
    //
    //  Antes paraba con un temporizador: `setTimeout(duración + 300ms)`. Dos
    //  problemas, y los dos se notan justo cuando esto importa:
    //
    //   · el reproductor tarda en arrancar después de un salto, y ese retraso
    //     el temporizador lo contaba como canción sonada — o sea que cortaba
    //     ANTES de que acabara el verso, que es exactamente «no hace nada»;
    //   · y los 300 ms de cola metían un pedazo del verso siguiente, que es lo
    //     contrario de lo que se viene a comprobar.
    //
    //  Ahora se mira el reloj, como en el clic sobre la línea, y se para en el
    //  corte exacto. Mientras suena se enseña por dónde va, porque si no no hay
    //  forma de saber si está sonando o si no hizo nada.
    const playSegment = useCallback(() => {
        if (!playerRef?.current || startTime === null) return;
        clearInterval(relojDelSegmento.current);
        relojDelSegmento.current = null;
        onStopPreview();                 // cancela cualquier preview del padre

        const corte = endTime !== null ? endTime : startTime + 5;
        setPlayingSegment(true);
        try {
            playerRef.current.seekTo(Math.max(0, startTime), true);
            playerRef.current.playVideo();
        } catch (_) { }

        relojDelSegmento.current = setInterval(() => {
            try {
                const t = playerRef.current.getCurrentTime();
                setDondeVa(t);
                if (t >= corte) {
                    playerRef.current.pauseVideo();
                    playerRef.current.seekTo(corte, true);
                    clearInterval(relojDelSegmento.current);
                    relojDelSegmento.current = null;
                    setPlayingSegment(false);
                    setDondeVa(null);
                    flash(`⏸ Fin del verso, ${formatTimeCompact(corte)}`);
                }
            } catch (_) { }
        }, 120);
    }, [playerRef, startTime, endTime, flash, onStopPreview]);

    const paraElSegmento = useCallback(() => {
        clearInterval(relojDelSegmento.current);
        relojDelSegmento.current = null;
        setPlayingSegment(false);
        setDondeVa(null);
        try { playerRef.current?.pauseVideo(); } catch (_) { }
    }, [playerRef]);

    //  Al cerrar el panel no puede quedarse un reloj suelto parando una canción
    //  que ya no es la suya.
    useEffect(() => () => clearInterval(relojDelSegmento.current), []);

    // ── Field row renderer ────────────────────────────────────────────
    const renderField = (field) => {
        const val = field === 'start' ? startTime : endTime;
        const isEditing = editField === field;
        const color = field === 'start' ? 'var(--green)' : 'var(--orange)';
        const label = field === 'start' ? 'INICIO' : 'FIN';
        const disabled = field === 'end' && (endTime === null || nextLine === null);
        const isPreviewThis = previewing?.idx === lineIdx && previewing?.side === field;

        //  Adónde devolvería el ↩, y si hay algo que devolver. Comparando con una
        //  centésima de margen: el tiempo se guarda en décimas, y un `!==` entre
        //  números con coma deja el botón encendido por un redondeo que nadie ve.
        const elOriginal = field === 'start' ? original.start : original.end;
        const sinCambios = elOriginal === null || elOriginal === undefined
            || val === null || Math.abs(val - elOriginal) < 0.05;

        return (
            <div
                className={`se-prec2-field-row ${disabled ? 'disabled' : ''}`}
                style={{ '--fc': color }}
            >
                <span className="se-prec2-lbl">{label}</span>

                {/* Time display / input */}
                {isEditing ? (
                    <input
                        className="se-prec-inp"
                        value={editVal}
                        autoFocus
                        placeholder="M:SS.d"
                        onChange={e => setEditVal(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') { commitEdit(field); return; }
                            if (e.key === 'Escape') { setEditField(null); return; }
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const sign = e.key === 'ArrowUp' ? 1 : -1;
                                const step = e.ctrlKey ? 10 : e.shiftKey ? 1 : 0.1;
                                nudge(field, sign * step);
                            }
                        }}
                        onBlur={() => commitEdit(field)}
                        onFocus={e => e.target.select()}
                    />
                ) : (
                    <button
                        className="se-prec-time-disp"
                        style={{ '--fc': color }}
                        onClick={() => {
                            if (disabled) return;
                            setEditVal(formatTimeCompact(val ?? 0));
                            setEditField(field);
                        }}
                        title={disabled ? 'Última línea — sin FIN' : 'Click para editar · Enter confirma · ↑↓ nudge'}
                    >
                        {val !== null ? formatTimeCompact(val) : '—'}
                    </button>
                )}

                {/* Nudge buttons */}
                <div className="se-prec2-nudges">
                    {NUDGE_STEPS.map(({ label: lbl, delta, cls }) => (
                        <button
                            key={lbl}
                            className={`se-prec-ndg ${cls}`}
                            onClick={() => nudge(field, delta)}
                            disabled={disabled}
                        >
                            {lbl}
                        </button>
                    ))}
                </div>

                {/* 🎧 Preview cut button — −3s antes del punto */}
                {isPreviewThis ? (
                    <button
                        className="se-preview-cut-btn playing"
                        onClick={onStopPreview}
                        title="Detener preview"
                    >
                        <span className="se-preview-pulse" />⏸
                    </button>
                ) : (
                    <button
                        className="se-preview-cut-btn"
                        onClick={() => !disabled && val !== null && onPreviewStart(lineIdx, val, field)}
                        disabled={disabled || val === null}
                        title={`Escuchar: 3s antes → ${formatTimeCompact(val ?? 0)} → 3s después → auto-pausa`}
                    >
                        🎧
                    </button>
                )}

                {/* Seek */}
                <button
                    className="se-prec-seek-btn"
                    onClick={() => !disabled && val !== null && seek(val)}
                    disabled={disabled || val === null}
                    title="Ir a este punto"
                >
                    ▶
                </button>

                {/* ↩ Volver al punto original — Rob: «un botón de regresar al punto
                    original para corregir mi error a un solo clic, puede ser al lado
                    del icono play y audífonos».

                    Lleva el número DENTRO: un ↩ pelado no dice adónde va, y para
                    cuando hace falta ya no se recuerda de dónde se partió — ahí
                    está el error de dedo —. Con el número se decide sin pulsarlo. */}
                <button
                    className={`se-prec-undo-btn${sinCambios ? ' limpio' : ''}`}
                    onClick={() => !disabled && !sinCambios && deshacer(field)}
                    disabled={disabled || sinCambios || elOriginal === null}
                    title={sinCambios
                        ? `Sin cambios: sigue en ${formatTimeCompact(elOriginal ?? 0)}`
                        : `Devolver a ${formatTimeCompact(elOriginal ?? 0)}, como estaba al abrir`
                          + (field === 'start' && arrastre.current !== 0
                              ? ' — y deshacer el empujón a las de abajo' : '')}
                >
                    ↩ {elOriginal !== null ? formatTimeCompact(elOriginal) : '—'}
                </button>
            </div>
        );
    };

    return (
        <div className="se-precision-panel" style={{ position: 'relative' }}>

            {/* Local toast */}
            {toastMsg && <div className="se-prec2-toast">{toastMsg}</div>}

            {/* ── LA LETRA, ARRIBA DEL TODO ─────────────────────────────
                Rob: «la letra de la canción tiene que ser visible, porque es donde
                yo voy leyendo, escuchando y empatando. Arriba del timeline
                debería venir la letra, lo que debería escuchar en ese fragmento
                para empatarlo preciso».

                Con la de antes y la de después en pequeño: lo que se está
                decidiendo aquí es DÓNDE ACABA UNA Y EMPIEZA LA OTRA, y para eso
                hacen falta las dos frases delante, no sólo una. */}
            <div className={`se-prec2-letra${playingSegment ? ' sonando' : ''}`}>
                {prevLine?.text && (
                    <div className="se-prec2-letra-vecina">↑ {prevLine.text}</div>
                )}
                <div className="se-prec2-letra-esta">
                    {line?.text || <span className="se-prec2-letra-vacia">(esta línea no tiene letra)</span>}
                </div>
                {nextLine?.text && (
                    <div className="se-prec2-letra-vecina">↓ {nextLine.text}</div>
                )}
            </div>

            {/* ── MINI TIMELINE ─────────────────────────────────────── */}
            <div className="se-prec2-tl-section">
                <div className="se-prec2-tl-top">
                    <span className="se-prec2-tl-title">
                        ⬤ Timeline — arrastra los handles · lo gris es la vecina: si pasas, se corre
                    </span>
                    {duration !== null && (
                        <span className={`se-prec2-dur-badge ${duration < 0 ? 'negative' : ''}`}>
                            ⏱ {Math.abs(r1(duration)).toFixed(1)}s{duration < 0 ? ' ⚠️' : ''}
                        </span>
                    )}
                </div>

                <div className="se-prec2-tl-wrap">
                    <div className="se-prec2-tl-track" ref={trackRef}>

                        {/* Dónde están las vecinas. ANTES esto se pintaba como zona
                            prohibida —rayado rojo— porque de verdad no se podía pasar
                            de ahí. Ahora sí se puede: pasar empuja a la vecina. Así
                            que se pinta en gris y sólo informa, porque una señal de
                            prohibido donde no hay prohibición frena a quien la lee
                            igual que la prohibición de verdad. */}
                        {topes.start.min > tlStart && (
                            <div className="se-prec2-vecina"
                                 title="Aquí está la línea de arriba. Si pasas, se corre."
                                 style={{ left: 0, width: `${tlPct(topes.start.min)}%` }} />
                        )}
                        {topes.end && topes.end.max < tlEnd && (
                            <div className="se-prec2-vecina"
                                 title="Aquí empieza la línea de abajo. Si pasas, se corre."
                                 style={{ left: `${tlPct(topes.end.max)}%`,
                                          right: 0, width: 'auto' }} />
                        )}

                        {/* Region highlight between START and END */}
                        {endPct !== null && (
                            <div
                                className="se-prec2-region"
                                style={{
                                    left: `${startPct}%`,
                                    width: `${Math.max(0.4, endPct - startPct)}%`,
                                }}
                            >
                                <div className="se-prec2-region-s" />
                                <div className="se-prec2-region-e" />
                            </div>
                        )}
                        {endPct === null && (
                            <div
                                className="se-prec2-region"
                                style={{ left: `${startPct}%`, width: `${100 - startPct}%`, opacity: 0.18 }}
                            />
                        )}

                        {/* START handle (green) */}
                        <div
                            className={`se-prec2-handle start${elegido === 'start' ? ' elegido' : ''}${moviendo === 'start' ? ' moviendo' : ''}`}
                            style={{ left: `${startPct}%` }}
                            onMouseDown={e => onHandleDown(e, 'start')}
                            onTouchStart={e => onHandleDown(e, 'start')}
                            title={`INICIO ${formatTimeCompact(startTime)} — clic para elegirlo · arrastra para moverlo · flechas para afinar`}
                        >
                            <div className="se-prec2-hline" />
                            <div className="se-prec2-hline" />
                            <div className="se-prec2-hline" />
                        </div>

                        {/* END handle (orange) — only when next line exists */}
                        {endTime !== null && endPct !== null && (
                            <div
                                className={`se-prec2-handle end${elegido === 'end' ? ' elegido' : ''}${moviendo === 'end' ? ' moviendo' : ''}`}
                                style={{ left: `${endPct}%` }}
                                onMouseDown={e => onHandleDown(e, 'end')}
                                onTouchStart={e => onHandleDown(e, 'end')}
                                title={`FIN = INICIO de la línea ${lineIdx + 2} (${formatTimeCompact(endTime)}) — moverlo la mueve a ella`}
                            >
                                <div className="se-prec2-hline" />
                                <div className="se-prec2-hline" />
                                <div className="se-prec2-hline" />
                            </div>
                        )}
                    </div>
                </div>

                <div className="se-prec2-tl-labels">
                    <span className="se-prec2-tl-l">{formatTimeCompact(tlStart)}</span>
                    <span className="se-prec2-tl-l">{formatTimeCompact((tlStart + tlEnd) / 2)}</span>
                    <span className="se-prec2-tl-l">{formatTimeCompact(tlEnd)}</span>
                </div>
            </div>

            {/* Cascade hint */}
            {nextLine && (
                <div className="se-prec2-cascade-hint">
                    <div className="se-prec2-cascade-dot" />
                    FIN de esta línea = INICIO de #{lineIdx + 2} (cascade automático)
                </div>
            )}

            {/* ── FIELD ROWS: INICIO + FIN ──────────────────────────── */}
            <div className="se-prec2-fields">
                {renderField('start')}
                {renderField('end')}
            </div>

            {/* ── ACTIONS ───────────────────────────────────────────── */}
            <div className="se-prec-actions">
                {/* ▶ NUEVO: Reproducir solo el segmento START→END */}
<button
                    className={`se-prec-preview-btn ${playingSegment ? 'playing' : ''}`}
                    onClick={playingSegment ? paraElSegmento : playSegment}
                    disabled={startTime === null}
                    title={endTime !== null
                        ? `Oír el verso entero: ${formatTimeCompact(startTime)} → ${formatTimeCompact(endTime)} `
                          + `(${r1(endTime - startTime).toFixed(1)}s) y parar justo ahí`
                        : 'Oír desde el INICIO (esta línea no tiene FIN: es la última)'}
                    style={{ fontWeight: 800, minWidth: 150 }}
                >
                    {playingSegment
                        ? (<><span className="se-preview-pulse" /> ⏸ Sonando
                            {dondeVa !== null && endTime !== null
                                ? ` · ${formatTimeCompact(Math.max(0, endTime - dondeVa))} para el final`
                                : ''}</>)
                        : `▶ Oír el verso${endTime !== null ? ` (${r1(endTime - startTime).toFixed(1)}s)` : ''}`}
                </button>

                {/* Propagate toggle — ya existente */}
                <label
                    className="se-prec-propagate"
                    title="Aplica el mismo delta de INICIO a todas las líneas siguientes"
                >
                    <input
                        type="checkbox"
                        checked={propagate}
                        onChange={e => setPropagate(e.target.checked)}
                    />
                    <span>Propagar INICIO a siguientes</span>
                </label>
            </div>

            {/* ── CONTEXT: prev / current / next ───────────────────── */}
            <div className="se-prec-context">
                {prevLine && (
                    <div className="se-prec-ctx-row prev">
                        <span className="se-prec-ctx-lbl">↑ Anterior</span>
                        <span className="se-prec-ctx-time">{formatTimeCompact(prevLine.time)}</span>
                        <span className="se-prec-ctx-text">{prevLine.text?.slice(0, 50)}</span>
                    </div>
                )}
                <div className="se-prec-ctx-row current">
                    <span className="se-prec-ctx-lbl">★ Esta</span>
                    <span className="se-prec-ctx-time" style={{ color: 'var(--green)' }}>
                        {formatTimeCompact(startTime)}
                    </span>
                    <span className="se-prec-ctx-text" style={{ color: 'var(--txt)' }}>
                        {line.text?.slice(0, 50)}{line.text?.length > 50 ? '…' : ''}
                    </span>
                </div>
                {nextLine && (
                    <div className="se-prec-ctx-row next">
                        <span className="se-prec-ctx-lbl">↓ Siguiente</span>
                        <span className="se-prec-ctx-time" style={{ color: 'var(--orange)' }}>
                            {formatTimeCompact(nextLine.time)}
                        </span>
                        <span className="se-prec-ctx-text">{nextLine.text?.slice(0, 50)}</span>
                    </div>
                )}
                {duration !== null && (
                    <div className="se-prec-duration">
                        ⏱ Duración de esta línea:{' '}
                        <strong style={{ color: duration < 0 ? 'var(--red)' : 'var(--cyan)' }}>
                            {r1(Math.abs(duration)).toFixed(2)}s
                        </strong>
                        {duration < 0 && (
                            <span style={{ color: 'var(--red)', marginLeft: 8 }}>
                                ⚠️ FIN antes que INICIO
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="se-prec-hint">
                🎧 = 3s antes → punto → 3s → auto-pausa · Shift+↑↓ = ±1s · Ctrl+↑↓ = ±10s · Enter confirma
                {nextLine && <><br />🔗 Mover FIN actualiza automáticamente INICIO de la línea siguiente</>}
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════

export default function SyncEditorTab({ token, encargo, tomaEncargo }) {
    // ─ State ─────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);
    //  Qué canción se está corrigiendo, si es que se está corrigiendo alguna.
    //  Con esto puesto, guardar escribe ENCIMA en vez de crear una copia.
    const [editandoId, setEditandoId] = useState(null);
    const [videoId, setVideoId] = useState('');
    const [title, setTitle] = useState('');
    const [artist, setArtist] = useState('');
    const [category, setCategory] = useState('pop');
    const [gameDifficulty, setGameDifficulty] = useState('intermediate');
    const [rawLyrics, setRawLyrics] = useState('');
    const [lines, setLines] = useState([]);
    const [activeLineIdx, setActiveLineIdx] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    //  Cuánto dura la canción. Hace falta para los topes: la última línea no
    //  tiene siguiente, así que su final es el final de la canción y sin este
    //  número no habría contra qué frenarla.
    const [duracionDeLaCancion, setDuracionDeLaCancion] = useState(0);

    /**
     * Qué línea mandó oír el usuario. Mientras esté puesta, el seguimiento
     * automático no toca la selección.
     *
     * Rob, 07-10-2026: «le doy clic para escuchar... veo que se sube y luego se
     * baja el color morado de selección. Si yo quiero dar mil veces clic para
     * escuchar la letra con el vídeo, al momento de darle clic se tiene que
     * quedar ahí... se ve raro, como que no sabe qué hacer».
     *
     * Y hacía exactamente eso. Al pulsar una línea se salta tres décimas ANTES
     * de su tiempo —para oír el ataque de la frase—, así que el seguimiento
     * automático miraba el reloj, veía que aún no había llegado, y se llevaba el
     * morado a la línea de arriba. Tres décimas después volvía. Un parpadeo por
     * cada clic, y son muchos clics.
     *
     * Elegir a mano gana: el seguimiento está para cuando la canción corre sola.
     */
    const mandaElUsuario = useRef(null);

    const [globalOffset, setGlobalOffset] = useState(0);
    const [followMode, setFollowMode] = useState(true);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ text: '', type: '' });

    // ── v5.2: precision panel state ──────────────────────────────────
    const [openPrecision, setOpenPrecision] = useState(new Set());

    // previewing = { idx: number, side: 'start' | 'end' } | null
    const [previewing, setPreviewing] = useState(null);
    const previewTimerRef = useRef(null);

    const { playerRef, createPlayer, destroyPlayer } = useYouTubePlayer();
    const ytContainer = useRef(null);
    const listRef = useRef(null);
    const linesRef = useRef(lines);
    useEffect(() => { linesRef.current = lines; }, [lines]);

    const activeLineIdxRef = useRef(activeLineIdx);
    useEffect(() => { activeLineIdxRef.current = activeLineIdx; }, [activeLineIdx]);

    // 🔒 Ref para saber si algún panel de precisión está abierto
    const openPrecisionRef = useRef(openPrecision);
    useEffect(() => { openPrecisionRef.current = openPrecision; }, [openPrecision]);

    // ─ Toast ──────────────────────────────────────────────────────────
    const showToast = (text, type = 'info') => {
        setToast({ text, type });
        setTimeout(() => setToast({ text: '', type: '' }), 3500);
    };

    /* ── LA VARITA DE SONG LIBRARY (07-10-2026) ───────────────────────
       Rob: «Song Library no tiene su boton de varita para editar la cancion en
       Sync Editor como lo tenemos en videos, y es genial».

       Llega por el buzon del dashboard: Song Library deja aqui la cancion
       entera y esta pantalla la coge UNA vez y lo vacia. Sin vaciarlo, salir de
       la pestaña y volver recargaria la misma cancion encima de lo que se
       estuviera corrigiendo.

       Se entra por el paso 2 —los tiempos— porque es a lo que se viene: los
       metadatos ya estan puestos y lo que se corrige es una silaba desfasada o
       un blanco mal puesto. */
    useEffect(() => {
        if (!encargo?.cancion) return;
        const d = hidrataCancion(encargo.cancion, createLineObj);
        setEditandoId(d.id);
        setVideoId(d.videoId);
        setTitle(d.title);
        setArtist(d.artist);
        setCategory(d.category);
        setGameDifficulty(d.gameDifficulty);
        setLines(d.lines);
        setRawLyrics(d.lines.map((l) => l.text).join('\n'));
        setActiveLineIdx(0);
        setGlobalOffset(0);
        setOpenPrecision(new Set());
        setStep(2);
        showToast(
            `✏️ «${d.title}» abierta para corregir — ${d.lineasLeidas} líneas `
            + `y ${d.blancosLeidos} blancos`,
            'success',
        );
        tomaEncargo?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encargo]);

    // ─ Player init ───────────────────────────────────────────────────
    useEffect(() => {
        if (step >= 2 && videoId) createPlayer('sync-yt-player', extractId(videoId));
        return () => destroyPlayer();
    }, [step, videoId, createPlayer, destroyPlayer]);

    // ─ Real-time tracker ─────────────────────────────────────────────
    useEffect(() => {
        if (step < 2) return;
        const timer = setInterval(() => {
            if (!playerRef.current) return;
            try {
                const t = playerRef.current.getCurrentTime();
                setCurrentTime(t);
                const dur = playerRef.current.getDuration?.();
                if (Number.isFinite(dur) && dur > 0) setDuracionDeLaCancion(dur);
                if (playerRef.current.getPlayerState() === 1) {
                    const cur = linesRef.current;
                    let best = -1;
                    for (let i = 0; i < cur.length; i++) {
                        if (cur[i].time !== null && cur[i].time <= t) best = i;
                    }
                    //  🔒 Ni con un panel de precisión abierto, ni mientras se esté
                    //  oyendo una línea que se eligió a mano. Ver `mandaElUsuario`.
                    if (best >= 0 && best !== activeLineIdxRef.current
                        && openPrecisionRef.current.size === 0
                        && mandaElUsuario.current === null) {
                        setActiveLineIdx(best);
                    }
                }
            } catch (_) { }
        }, 100);
        return () => clearInterval(timer);
    }, [step, playerRef]);

    // ─ Auto-scroll ───────────────────────────────────────────────────
    useEffect(() => {
        if (!followMode || !listRef.current) return;
        const el = listRef.current.querySelectorAll('.se-line')[activeLineIdx];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [activeLineIdx, followMode]);

    // ─ Spacebar stamp ────────────────────────────────────────────────
    useEffect(() => {
        const handle = (e) => {
            if (step !== 2) return;
            if (e.code !== 'Space') return;
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            e.preventDefault();
            if (!playerRef.current) return;
            try {
                const t = playerRef.current.getCurrentTime();
                const currentIdx = activeLineIdxRef.current;
                setLines(prev => {
                    const n = [...prev];
                    if (n[currentIdx]) n[currentIdx] = { ...n[currentIdx], time: t };
                    return n;
                });
                // 🔒 Solo avanzar si no hay precision panels abiertos
                if (openPrecisionRef.current.size === 0) {
                    setActiveLineIdx(prev => Math.min(prev + 1, linesRef.current.length - 1));
                }
            } catch (_) { }
        };
        window.addEventListener('keydown', handle);
        return () => window.removeEventListener('keydown', handle);
    }, [step, playerRef]);

    // ══════════════════════════════════════════════════════════════════
    //  v5.2: PRECISION OPERATIONS
    // ══════════════════════════════════════════════════════════════════

    const togglePrecision = (idx) => {
        setOpenPrecision(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    // 🎧 Oír la entrada de un corte.
    // side: 'start' | 'end'
    /**
     * Oír la entrada de un corte: desde unos segundos antes hasta unos después,
     * y parar solo.
     *
     * Para mirando el reloj, no con un temporizador. Un `setTimeout` cuenta
     * también lo que el reproductor tarda en arrancar después de un salto —que
     * es casi un segundo—, así que cortaba antes de llegar y lo que se quería
     * comprobar no llegaba a sonar.
     */
    const previewCut = useCallback((lineIdx, time, side = 'start', antes = PREVIEW_PRE) => {
        if (!playerRef.current || time === null) return;
        clearInterval(previewTimerRef.current);
        setPreviewing({ idx: lineIdx, side });

        //  Esto arranca ANTES del corte a propósito —se viene a oír llegar la
        //  frase—, o sea que sin esta marca el seguimiento se llevaría el morado a
        //  la línea de arriba durante esos segundos.
        //
        //  Y se elige la línea: oír su entrada es mirarla. Dejar el morado en otra
        //  mientras suena ésta es la misma confusión que él describió con el clic,
        //  sólo que quieta en vez de parpadeando.
        mandaElUsuario.current = lineIdx;
        setActiveLineIdx(lineIdx);
        const desde = Math.max(0, time - antes);
        const hasta = time + PREVIEW_POST;
        try {
            playerRef.current.seekTo(desde, true);
            playerRef.current.playVideo();
        } catch (_) { }

        previewTimerRef.current = setInterval(() => {
            try {
                if (playerRef.current.getCurrentTime() >= hasta) {
                    playerRef.current.pauseVideo();
                    clearInterval(previewTimerRef.current);
                    previewTimerRef.current = null;
                    mandaElUsuario.current = null;
                    setPreviewing(null);
                    showToast(
                        `⏸ Oído el ${side === 'start' ? 'INICIO' : 'FIN'} de ${formatTimeCompact(time)}`,
                        'info',
                    );
                }
            } catch (_) { }
        }, 120);
    }, [playerRef]);

    const stopPreview = useCallback(() => {
        clearInterval(previewTimerRef.current);
        previewTimerRef.current = null;
        mandaElUsuario.current = null;
        setPreviewing(null);
        try { playerRef.current?.pauseVideo(); } catch (_) { }
        showToast('⏸ Preview detenido', 'info');
    }, [playerRef]);

    // Nudge with optional propagation to subsequent lines
    const nudgeWithPropagation = useCallback((lineIdx, delta, propagate = false) => {
        setLines(prev => {
            const next = [...prev];
            if (!next[lineIdx] || next[lineIdx].time === null) return prev;
            next[lineIdx] = { ...next[lineIdx], time: Math.max(0, next[lineIdx].time + delta) };
            if (propagate) {
                for (let i = lineIdx + 1; i < next.length; i++) {
                    if (next[i].time !== null) {
                        next[i] = { ...next[i], time: Math.max(0, next[i].time + delta) };
                    }
                }
                showToast(
                    `⏱ ${delta > 0 ? '+' : ''}${delta.toFixed(1)}s propagado a ${next.length - lineIdx - 1} líneas`,
                    'success'
                );
            }
            return next;
        });
    }, []);

    // Set exact time for any line (used by LinePrecisionPanel for both START and END cascade)
    /**
     * Mover una línea empujando a las vecinas lo justo para que quepan.
     *
     * Rob: «necesita todavía unos segundos más para cubrir la parte de la letra.
     * Le di a +0.5 y ya no me dejó. Como que me castigó y dijo: hasta aquí es mi
     * límite».
     *
     * Frenar en seco protege a la vecina y rompe el trabajo: si el verso dura de
     * verdad hasta el 0:31.5, la línea de abajo EMPIEZA en el 0:31.5 — su tiempo
     * de ahora está mal, y es lo que hay que corregir, no lo que hay que
     * defender.
     *
     * Devuelve cuáles se movieron además de la pedida, para poder decirlo y para
     * poder deshacerlo.
     */
    //  La cuenta se hace AQUÍ FUERA, con las líneas de ahora, y no dentro del
    //  actualizador de `setLines`. React no ejecuta el actualizador en el acto, así
    //  que sacar un dato de dentro devuelve lo que había antes de entrar — aquí,
    //  una lista vacía de «cuáles se corrieron». Resultado: no se avisaba de nada y
    //  el ↩ no sabía a quién devolver. Es el mismo tropiezo que el desfase global.
    const acomodaLaLinea = useCallback((lineIdx, nuevoTiempo) => {
        const r = acomoda(lines, lineIdx, nuevoTiempo, duracionDeLaCancion);
        setLines(r.lines);
        return r.movidas;
    }, [lines, duracionDeLaCancion]);

    const setLineTime = useCallback((lineIdx, newTime, propagate = false) => {
        setLines(prev => {
            if (lineIdx < 0 || lineIdx >= prev.length) return prev;
            const next = [...prev];
            const oldTime = next[lineIdx].time;
            const delta = oldTime !== null ? newTime - oldTime : 0;
            next[lineIdx] = { ...next[lineIdx], time: newTime };
            if (propagate && delta !== 0) {
                for (let i = lineIdx + 1; i < next.length; i++) {
                    if (next[i].time !== null) {
                        next[i] = { ...next[i], time: Math.max(0, next[i].time + delta) };
                    }
                }
            }
            return next;
        });
    }, []);

    // ══════════════════════════════════════════════════════════════════
    //  BACKEND-POWERED PARSER
    // ══════════════════════════════════════════════════════════════════
    //  Lo que el servidor encontró: formato, precisión y qué hacer para
    //  mejorarla. Se enseña arriba del paso 2 porque es ANTES de sincronizar
    //  cuando sirve de algo: descubrir a mitad de canción que el panel de
    //  YouTube no trae milisegundos es descubrirlo tarde.
    const [informe, setInforme] = useState(null);
    //  El consejo entero, sólo cuando se pide. Cerrado por defecto: se lee una vez
    //  al empezar y después estorba durante toda la sesión.
    const [informeAbierto, setInformeAbierto] = useState(false);

    const handleParseTranscript = async () => {
        setLoading(true);
        const authToken = token || localStorage.getItem('token');
        try {
            const res = await fetch('/api/admin/sync-editor/parse-transcript', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ raw_text: rawLyrics }),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            //  `estimado` viaja con cada línea: son los versos cuyo tiempo se
            //  DEDUJO repartiendo, no venía en la fuente. Marcarlos es lo que
            //  convierte «revisa la canción entera» en «revisa estas cuatro».
            const enriched = data.lines.map((l, i) => ({
                ...createLineObj(i, l.text, l.time),
                estimado: Boolean(l.estimado),
                //  Las frases que venían juntas bajo este tiempo. Con más de una,
                //  la línea ofrece partirse — ver `parteLaLinea`.
                frases: Array.isArray(l.frases) && l.frases.length > 1 ? l.frases : null,
            }));
            setLines(enriched);
            setInforme(data.informe || null);
            setActiveLineIdx(0);
            setGlobalOffset(0);
            setOpenPrecision(new Set());
            setPreviewing(null);
            setStep(2);
            const conMs = data.informe?.precision_ms === 1;
            showToast(
                `✅ ${enriched.length} líneas · ${conMs ? 'milisegundos reales' : 'segundos enteros'}`,
                'success');
        } catch {
            showToast('❌ Error al parsear transcript', 'error');
        }
        setLoading(false);
    };

    // ── Fallback smart parser ─────────────────────────────────────────
    const parseSmartLyrics = () => {
        const normalized = rawLyrics
            .replace(/(\d{1,2}:\d{2})(\d)/g, '$1\n$2')
            .replace(/((?:\d+\s+minutes?,\s*)?\d+\s+seconds?)([♪(A-Za-z])/g, '$1\n$2')
            .replace(/Sync to video time/gi, '');

        const rawArray = normalized.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        const result = [];
        let cTime = null;
        let idx = 0;

        const pushText = (raw, time) => {
            if (isStageDirection(raw)) return;
            if (raw.includes('♪') || raw.includes('♫')) {
                const frags = raw.split(/[♪♫]/).map(s => s.trim()).filter(s => s.length > 1);
                for (const frag of frags) {
                    if (isStageDirection(frag)) continue;
                    const text = frag.replace(/[♪♫]/g, '').replace(/\s+/g, ' ').trim();
                    if (text.length > 1) result.push(createLineObj(idx++, text, time));
                }
            } else {
                const text = raw.replace(/[♪♫]/g, '').replace(/\s+/g, ' ').trim();
                if (text.length > 1) result.push(createLineObj(idx++, text, time));
            }
        };

        for (const line of rawArray) {
            const lrcM = line.match(/\[(\d+):(\d+(?:\.\d+)?)\](.*)/);
            if (lrcM) {
                const t = +lrcM[1] * 60 + parseFloat(lrcM[2]);
                const text = lrcM[3].replace(/[♪♫]/g, '').trim();
                if (text) result.push(createLineObj(idx++, text, t));
                continue;
            }
            if (/^\d{1,2}:\d{2}$/.test(line)) {
                const [min, sec] = line.split(':').map(Number);
                cTime = min * 60 + sec;
                continue;
            }
            if (/^\d{1,2}:\d{2}:\d{2}$/.test(line)) {
                const [h, min, sec] = line.split(':').map(Number);
                cTime = h * 3600 + min * 60 + sec;
                continue;
            }
            const accT = parseAccessibilityTime(line);
            if (accT !== null) { if (cTime !== null && Math.abs(accT - cTime) <= 2) cTime = accT; continue; }
            pushText(line, cTime);
        }

        if (result.length === 0) { showToast('⚠️ No se encontró letra válida.', 'error'); return; }
        setLines(result);
        setActiveLineIdx(0);
        setGlobalOffset(0);
        setOpenPrecision(new Set());
        setStep(2);
        showToast(`✅ ${result.length} líneas parseadas.`, 'success');
    };

    // ── Timing operations ─────────────────────────────────────────────
    //  ── LA FILA DE AL LADO DE CADA LÍNEA (07-10-2026) ────────────────
    //  Movía la línea sin mirar a nadie: cinco toques de +0.5 y la línea 1 se
    //  plantaba en el 5.5 con la 2 en el 4.2 — empezando DESPUÉS que la siguiente.
    //  Y se guardaba así, sin protestar. En el editor no se nota; lo nota el
    //  alumno, que recibe los huecos cambiados de sitio.
    //
    //  Ahora pasa por los mismos topes que el panel de precisión: no había
    //  ninguna razón para que dos botones que hacen lo mismo obedecieran a reglas
    //  distintas, y la que no tenía reglas era la que estaba siempre a la vista.

    //  ── EL DESFASE GLOBAL MUEVE LA CANCIÓN ENTERA ────────────────────
    //  Rob: «quise aumentar +0.5, le di clic y pum, modificó el inicio y el
    //  final». Era esto: sus botones se llaman igual que los de una línea —+0.5—
    //  y están al lado, pero mueven las cuarenta y siete a la vez. Un clic de más
    //  aquí descoloca la canción entera, y deshacerlo a mano es la tarde perdida
    //  que él contó.
    //
    //  El recorte es lo que hace que se pueda deshacer: con `Math.max(0, …)` por
    //  línea, un −10s aplastaba contra el cero a todas las que empezaban antes
    //  del 0:10 — tres líneas distintas en el mismo sitio, y ya sin forma de saber
    //  dónde estaba cada una. Ahora se mueve sólo lo que cabe.
    //  La cuenta se hace AQUÍ FUERA, no dentro del actualizador de `setLines`.
    //  Dentro, React lo ejecuta dos veces a propósito para cazar efectos
    //  escondidos — y el `setGlobalOffset` de en medio era uno: el desfase se
    //  apuntaba el doble de lo que se había movido, así que el botón de quitarlo
    //  devolvía un segundo donde sólo había medio. Lo cazó la batería.
    const applyGlobalOffset = (delta) => {
        const cabe = desfaseQueCabe(lines, delta);
        if (cabe === 0) {
            showToast('⛔ No cabe: la primera línea ya está en el 0:00', 'info');
            return;
        }
        if (Math.abs(cabe - delta) > 0.05) {
            showToast(`⛔ Sólo caben ${cabe}s: la primera línea llegaría al 0:00`, 'info');
        }
        setGlobalOffset(g => +(g + cabe).toFixed(3));
        setLines(prev => prev.map(l => (l.time !== null
            ? { ...l, time: aDecima(l.time + cabe) } : l)));
    };

    /** Devolver la canción entera a donde estaba antes de tocar el desfase. */
    const quitaElDesfase = () => {
        if (!globalOffset) return;
        const vuelta = -globalOffset;
        setLines(prev => prev.map(l => (l.time !== null
            ? { ...l, time: Math.max(0, aDecima(l.time + vuelta)) } : l)));
        setGlobalOffset(0);
        showToast(`↩ Desfase quitado: la canción vuelve ${vuelta > 0 ? '+' : ''}${vuelta.toFixed(1)}s`, 'success');
    };

    /**
     * Partir una línea en las frases que YouTube juntó bajo un mismo tiempo.
     *
     * Rob, 10-10-2026: «sólo quiero copiar y pegar y que se respeten los
     * tiempos».
     *
     * El panel de YouTube da un tiempo por bloque y mete dos o tres frases
     * dentro. Antes el servidor les inventaba un tiempo a las de dentro y salían
     * adelantadas: días de trabajo cuadrándolas a mano. Ahora no se inventa
     * nada, y partir es cosa de quien oye la canción.
     *
     * La primera frase se queda con el tiempo —que es real— y las de abajo salen
     * SIN tiempo, en gris, para marcarlas con la barra espaciadora mientras
     * suena. Es la única forma honesta de ponerles una hora.
     */
    const parteLaLinea = (idx) => {
        const l = lines[idx];
        const frases = l?.frases;
        if (!frases || frases.length < 2) return;
        setLines(prev => {
            const cabeza = createLineObj(prev[idx].idx, frases[0], prev[idx].time);
            const cola = frases.slice(1).map(f => ({
                //  Sin tiempo a propósito. Ver arriba.
                ...createLineObj(0, f, null),
            }));
            const next = [...prev.slice(0, idx), cabeza, ...cola, ...prev.slice(idx + 1)];
            //  Los índices se renumeran: son la posición en la lista, y acaba de
            //  cambiar para todas las de abajo.
            return next.map((x, i) => ({ ...x, idx: i }));
        });
        setActiveLineIdx(idx + 1);
        showToast(
            `✂ Partida en ${frases.length}. Las ${frases.length - 1} de abajo están `
            + 'sin marcar: púlsales la barra espaciadora cuando suenen.',
            'info',
        );
    };

    /**
     * Clic en una línea: suena SU TRAMO y para donde acaba.
     *
     * Rob, 07-10-2026: «ahora lo mismo de dar clic y se reproduce esa sección y
     * hace la pausa ahí para ir midiendo lo que encaja de inicio y final».
     *
     * Antes saltaba al principio de la línea y seguía sonando hasta que alguien
     * la parara. Para medir si una línea encaja hay que oírla ENTERA y que se
     * pare sola al acabar: si sigue corriendo, lo que se oye ya es la siguiente
     * y no hay forma de saber si el corte estaba bien puesto.
     *
     * El final de una línea es el principio de la siguiente —en una canción no
     * hay dos números, hay uno—. La última no tiene siguiente: esa suena hasta
     * el final de la canción, como siempre.
     */
    const pararEnElCorte = useRef(null);


    const seekToLine = (time, idx) => {
        setActiveLineIdx(idx);
        //  A partir de aquí manda él: el seguimiento automático no vuelve a tocar
        //  la selección hasta que esto termine.
        mandaElUsuario.current = idx;
        if (time === null || !playerRef.current) { mandaElUsuario.current = null; return; }
        clearInterval(pararEnElCorte.current);
        pararEnElCorte.current = null;
        try {
            //  Tres décimas antes: el reproductor no cae en el milisegundo que se
            //  le pide, y empezar un pelín antes deja oír el ataque de la frase.
            playerRef.current.seekTo(Math.max(0, time - 0.3), true);
            playerRef.current.playVideo();
        } catch (_) { }

        const corte = lines[idx + 1]?.time;
        //  La última línea no tiene corte: suena hasta el final de la canción y
        //  el seguimiento vuelve a mandar en cuanto se suelte.
        if (!Number.isFinite(corte)) { mandaElUsuario.current = null; return; }

        //  Se mira el reloj, no se pone un temporizador: el reproductor puede
        //  tardar en arrancar, y un temporizador contaría ese retraso como
        //  canción sonada y pararía antes de tiempo.
        pararEnElCorte.current = setInterval(() => {
            try {
                if (playerRef.current.getCurrentTime() >= corte) {
                    playerRef.current.pauseVideo();
                    playerRef.current.seekTo(corte, true);
                    clearInterval(pararEnElCorte.current);
                    pararEnElCorte.current = null;
                    mandaElUsuario.current = null;
                    showToast(`⏸ Fin de la línea ${idx + 1}, ${formatTimeCompact(corte)}`, 'info');
                }
            } catch (_) { }
        }, 150);
    };

    //  Al salir no puede quedarse un reloj suelto parando una canción que ya no
    //  es la suya.
    useEffect(() => () => clearInterval(pararEnElCorte.current), []);

    const changeSpeed = (rate) => {
        setPlaybackRate(rate);
        if (playerRef.current) { try { playerRef.current.setPlaybackRate(rate); } catch (_) { } }
    };

    // ── Blank operations ──────────────────────────────────────────────
    const toggleBlank = (lineIdx, wordIdx) => {
        setLines(prev => prev.map((l, li) => {
            if (li !== lineIdx) return l;
            return {
                ...l,
                words: l.words.map((w, wi) => {
                    if (wi !== wordIdx || w.clean.length < 2) return w;
                    const isBlank = !w.isBlank;
                    return { ...w, isBlank, choices: isBlank ? w.choices : [], hasDistractors: isBlank ? w.hasDistractors : false };
                }),
            };
        }));
    };

    const autoBlankByDifficulty = (diff) => {
        const cfg = DIFF_CONFIG[diff];
        setLines(prev => prev.map(line => {
            let count = 0;
            return {
                ...line,
                words: line.words.map(w => {
                    if (!w.level || !cfg.targets.includes(w.level) || count >= cfg.maxPerLine) {
                        return { ...w, isBlank: false };
                    }
                    count++;
                    return { ...w, isBlank: true };
                }),
            };
        }));
        showToast(`🎯 Auto-blanks modo ${cfg.label} aplicados`, 'success');
    };

    const clearAllBlanks = () => {
        setLines(prev => prev.map(l => ({
            ...l,
            words: l.words.map(w => ({ ...w, isBlank: false, choices: [], hasDistractors: false })),
        })));
        showToast('🧹 Todos los blancos limpiados', 'info');
    };

    const generateDistractors = async (lineIdx, wordIdx) => {
        const line = linesRef.current[lineIdx];
        const word = line?.words[wordIdx];
        if (!word) return;
        const authToken = token || localStorage.getItem('token');
        try {
            const res = await fetch('/api/admin/sync-editor/generate-distractors', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify({ word: word.clean, context_line: line.text }),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setLines(prev => prev.map((l, li) => {
                if (li !== lineIdx) return l;
                return {
                    ...l,
                    words: l.words.map((w, wi) =>
                        wi === wordIdx ? { ...w, choices: data.distractors, hasDistractors: true } : w
                    ),
                };
            }));
        } catch {
            showToast(`❌ Error generando distractores para "${word.clean}"`, 'error');
        }
    };

    const generateAllDistractors = async () => {
        const blankedWords = [];
        linesRef.current.forEach((line, lIdx) => {
            line.words.forEach((w, wIdx) => {
                if (w.isBlank && !w.hasDistractors) blankedWords.push({ lIdx, wIdx });
            });
        });
        if (blankedWords.length === 0) { showToast('✅ Todos los blancos ya tienen distractores.', 'success'); return; }
        showToast(`⏳ Generando distractores para ${blankedWords.length} palabras...`, 'info');
        setLoading(true);
        for (const { lIdx, wIdx } of blankedWords) {
            await generateDistractors(lIdx, wIdx);
            await new Promise(r => setTimeout(r, 600));
        }
        setLoading(false);
        showToast('🎉 ¡Todos los distractores generados!', 'success');
    };

    // ── Computed stats ────────────────────────────────────────────────
    const stats = useMemo(() => {
        let total = 0, blanked = 0;
        const byLevel = { basic: 0, intermediate: 0, advanced: 0, expert: 0 };
        const withDistractors = [];
        for (const line of lines) {
            for (const w of line.words) {
                if (!w.level) continue;
                total++;
                if (w.isBlank) {
                    blanked++;
                    byLevel[w.level] = (byLevel[w.level] || 0) + 1;
                    if (w.hasDistractors) withDistractors.push(w.clean);
                }
            }
        }
        return { total, blanked, byLevel, coverage: total > 0 ? Math.round(blanked / total * 100) : 0, withDistractors: withDistractors.length };
    }, [lines]);

    const timeline = useMemo(() => {
        const stamped = lines.filter(l => l.time !== null).map(l => l.time);
        const total = stamped.length > 0 ? Math.max(...stamped) + 15 : 300;
        return {
            total,
            segments: lines.map((l, i) => ({
                idx: i, pct: l.time !== null ? (l.time / total) * 100 : null,
                blanks: l.words.filter(w => w.isBlank).length, text: l.text, time: l.time,
            })),
        };
    }, [lines]);

    // ── Save ──────────────────────────────────────────────────────────
    const saveToDB = async () => {
        //  ── LA ÚLTIMA RED (07-10-2026) ───────────────────────────────
        //  Una línea que empieza igual o después que la siguiente se guardaba sin
        //  una palabra. En el editor no se ve: las líneas salen en el orden de la
        //  letra, no en el del reloj. Lo ve el alumno, al que le llega el hueco
        //  cambiado de sitio o un verso que dura menos que nada.
        //
        //  Los topes de los botones ya no dejan llegar ahí, pero esto se queda:
        //  una canción vieja puede traerlo de antes, y avisar cuesta una línea.
        const revueltas = lineasDesordenadas(lines);
        if (revueltas.length > 0) {
            const cuales = revueltas.slice(0, 3)
                .map(r => `   · la ${r.idx + 1} empieza en ${formatTimeCompact(r.time)} `
                    + `y la ${r.idx + 2} en ${formatTimeCompact(r.siguiente)}`)
                .join('\n');
            const masTexto = revueltas.length > 3 ? '\n   · …y alguna más' : '';
            const sigo = window.confirm(
                `⚠️ Hay ${revueltas.length} línea(s) fuera de orden:\n\n`
                + cuales + masTexto
                + '\n\nAsí el alumno recibe los huecos cambiados de sitio.'
                + '\n\n¿Guardar de todas formas?');
            if (!sigo) return;
        }

        const blanksWithout = lines.reduce((acc, l) => acc + l.words.filter(w => w.isBlank && !w.hasDistractors).length, 0);
        if (blanksWithout > 0) {
            const go = window.confirm(`⚠️ Hay ${blanksWithout} blancos sin distractores.\n\n`
                + (editandoId ? '¿Guardar de todas formas?' : '¿Publicar de todas formas?'));
            if (!go) return;
        }
        setLoading(true);
        const authToken = token || localStorage.getItem('token');
        //  La forma que se guarda vive en `hidrataCancion.js`, al lado de su
        //  contrario. Juntas se puede medir el viaje de ida y vuelta: abrir una
        //  canción y volver a guardarla tiene que dejarla igual.
        const finalSyncData = sincronizaParaGuardar(lines);
        try {
            //  Corrigiendo: se escribe ENCIMA. Antes esto era siempre un POST,
            //  así que una canción abierta para corregir se habría publicado
            //  otra vez al lado de la original, con los mismos título y artista
            //  y sin nada que dijera cuál era cuál.
            const res = editandoId
                ? await fetch(`/api/admin/sync-editor/${editandoId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                    body: JSON.stringify({
                        youtube_id: extractId(videoId), title, artist,
                        difficulty: gameDifficulty, category, sync_data: finalSyncData,
                    }),
                })
                : await fetch('/api/admin/sync-editor/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                    body: JSON.stringify({
                        youtube_id: extractId(videoId), title, artist,
                        difficulty: gameDifficulty, category, sync_data: finalSyncData,
                    }),
                });
            if (!res.ok) throw new Error();
            if (editandoId) {
                //  Y NO se vacía la pantalla: se ha venido a corregir, y lo más
                //  probable es que haya otra cosa que tocar. Vaciarla obligaría
                //  a volver a la biblioteca y entrar otra vez.
                showToast('💾 Cambios guardados en la canción', 'success');
            } else {
                showToast('🏆 ¡Canción publicada exitosamente!', 'success');
                setTimeout(() => { setStep(1); setLines([]); setRawLyrics(''); setGlobalOffset(0); }, 2500);
            }
        } catch {
            showToast('❌ Error al guardar. Verifica la conexión.', 'error');
        }
        setLoading(false);
    };

    // ══════════════════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════════════════
    const STEPS = [
        { num: 1, label: 'Metadata' },
        { num: 2, label: 'Timestamps' },
        { num: 3, label: 'Blancos' },
        { num: 4, label: 'Preview' },
    ];

    const needlePct = Math.min((currentTime / (timeline.total || 1)) * 100, 100);
    const precisionOpen = openPrecision.size;

    return (
        <div className="se-root">

            {/* ── HEADER ── */}
            <header className="se-header">
                {/* ── LO QUE SE LEYÓ, EN EL HUECO DE LA IZQUIERDA ────────
                    Rob: «me está robando espacio de pantalla... justamente cuando
                    quiero ver la letra y hacer modificaciones, este texto se
                    queda fijo. ¿Lo podemos poner a un lado de Pro Sync Studio?
                    Tenemos espacios muertos que se pueden aprovechar».

                    Tenía razón: era un cartel de dos líneas, fijo, encima del
                    editor — y lo que hay debajo es justo donde se trabaja. El
                    dato importa («segundos enteros» cambia lo que se puede
                    esperar de la precisión), así que no se quita: se encoge a una
                    pastilla y el consejo entero se abre al pulsarla. */}
                {step >= 2 && informe && (
                    <div className="se-informe-hueco">
                        <button
                            className={`se-informe-chip se-informe-${informe.precision_ms === 1 ? 'fino' : 'grueso'}`}
                            data-prueba="sync-informe"
                            onClick={() => setInformeAbierto(a => !a)}
                            title="Qué se leyó del transcript — pulsa para el detalle"
                        >
                            <span aria-hidden="true">{informe.precision_ms === 1 ? '🎯' : '⏱️'}</span>
                            <span>
                                {informe.lineas} líneas · {informe.formato.toUpperCase()}
                                {informe.precision_ms === 1 ? ' · ms reales' : ' · segundos enteros'}
                                {/* Ya no hay tiempos estimados: no se inventa ninguno.
                                    Lo que queda por hacer son las líneas que traen
                                    varias frases bajo un mismo tiempo. */}
                                {informe.juntas > 0 && ` · ${informe.juntas} por partir`}
                            </span>
                            <span className="se-informe-flecha">{informeAbierto ? '▴' : '▾'}</span>
                        </button>
                        {informeAbierto && (
                            <div className="se-informe-detalle">
                                {informe.consejo}
                                <button className="se-informe-cerrar"
                                        onClick={() => setInformeAbierto(false)}>Entendido</button>
                            </div>
                        )}
                    </div>
                )}

                <div className="se-brand">
                    <div className="se-brand-icon">🎛️</div>
                    <div>
                        <div className="se-brand-title">Pro Sync Studio</div>
                        <div className="se-brand-sub">NSL Lyrics Engine v5.2</div>
                    </div>
                </div>

                {/* ── QUÉ CANCIÓN SE ESTÁ CORRIGIENDO ────────────────────────
                    Con esto puesto, guardar escribe ENCIMA de esa canción. Hay
                    que poder verlo y hay que poder salirse: si no, se corrige
                    una, se empieza otra desde cero en la misma pantalla y al
                    guardarla se machaca la primera — que ya estaba publicada y
                    que nadie pedía tocar. */}
                {editandoId && (
                    <div className="se-corrigiendo">
                        <span>✏️ Corrigiendo <strong>{title || 'una canción'}</strong></span>
                        <button
                            className="se-corrigiendo-salir"
                            onClick={() => {
                                setEditandoId(null);
                                setStep(1); setLines([]); setRawLyrics(''); setGlobalOffset(0);
                                setVideoId(''); setTitle(''); setArtist('');
                                setActiveLineIdx(0); setOpenPrecision(new Set());
                                showToast('🆕 Lista para una canción nueva', 'info');
                            }}
                            title="Dejar de corregir ésta y empezar una canción nueva"
                        >
                            ✖ Empezar una nueva
                        </button>
                    </div>
                )}

                <nav className="se-stepper">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.num}>
                            <button
                                className={`se-step-pill ${step === s.num ? 'active' : step > s.num ? 'done' : ''}`}
                                onClick={() => step > s.num && setStep(s.num)}
                            >
                                <span className="se-step-n">{step > s.num ? '✓' : s.num}</span>
                                <span>{s.label}</span>
                            </button>
                            {i < STEPS.length - 1 && (
                                <div className={`se-step-connector ${step > s.num ? 'done' : ''}`} />
                            )}
                        </React.Fragment>
                    ))}
                </nav>

                {step >= 3 && (
                    <div className="se-header-stats">
                        {Object.entries(stats.byLevel).map(([lvl, cnt]) => cnt > 0 && (
                            <div key={lvl} className="se-stat-badge" style={{ '--c': DIFF_CONFIG[lvl].color }}>
                                {DIFF_CONFIG[lvl].emoji} {cnt}
                            </div>
                        ))}
                        <div className="se-stat-badge" style={{ '--c': '#a78bfa' }}>📊 {stats.coverage}%</div>
                    </div>
                )}
                {step === 2 && precisionOpen > 0 && (
                    <div className="se-stat-badge" style={{ '--c': '#06b6d4', marginLeft: 'auto' }}>
                        ⚡ {precisionOpen} en precisión
                    </div>
                )}
            </header>

            {/* ── TOAST ── */}
            {toast.text && (
                <div className={`se-toast se-toast--${toast.type}`}>{toast.text}</div>
            )}

            <div className="se-body">

                {/* ════════════ STEP 1 ════════════ */}
                {step === 1 && (
                    <div className="se-step1">
                        <div className="se-meta-grid">
                            <div className="se-field span2">
                                <label>YouTube URL o ID del video</label>
                                <input value={videoId} onChange={e => setVideoId(e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                            </div>
                            <div className="se-field">
                                <label>Título de la canción</label>
                                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Here Comes the Sun" />
                            </div>
                            <div className="se-field">
                                <label>Artista / Banda</label>
                                <input value={artist} onChange={e => setArtist(e.target.value)} placeholder="The Beatles" />
                            </div>
                            <div className="se-field">
                                <label>Categoría</label>
                                <select value={category} onChange={e => setCategory(e.target.value)}>
                                    <option value="pop">🎵 Pop</option>
                                    <option value="rock">🎸 Rock</option>
                                    <option value="soul">🎷 R&B / Soul</option>
                                    <option value="indie">🌙 Indie</option>
                                    <option value="classic">🏛️ Clásicos</option>
                                    <option value="country">🤠 Country</option>
                                    <option value="electronic">⚡ Electronic</option>
                                    <option value="folk">🎻 Folk</option>
                                </select>
                            </div>
                            <div className="se-field">
                                <label>Dificultad de la canción</label>
                                <div className="se-diff-row">
                                    {Object.entries(DIFF_CONFIG).map(([key, cfg]) => (
                                        <button
                                            key={key}
                                            className={`se-diff-btn ${gameDifficulty === key ? 'active' : ''}`}
                                            style={{ '--dc': cfg.color, '--ddk': cfg.darkColor }}
                                            onClick={() => setGameDifficulty(key)}
                                        >
                                            {cfg.emoji} {cfg.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="se-lyrics-card">
                            <div className="se-lyrics-header">
                                <div>
                                    <div className="se-field-label">Transcript / Letra</div>
                                    <div className="se-field-hint">
                                        Soporta: <code>0:14</code> multilínea · <code>"1 minute, 6 seconds"</code> · LRC <code>[00:14.50]</code> · notas ♪
                                    </div>
                                </div>
                            </div>
                            {/* La caja coloreada: el tiempo salta a la vista, la
                                duración duplicada de YouTube se apaga y las
                                acotaciones dejan de competir con la letra. Es la
                                misma que usa el Video Edu — una caja, un arreglo.
                                Y lleva su propia cuenta de líneas, más útil que
                                la de aquí: dice cuántas tienen tiempo. */}
                            <CajaDeTranscripcion
                                valor={rawLyrics}
                                alCambiar={setRawLyrics}
                                prefijo="sync"
                                placeholder={`Pega aquí la letra con sus tiempos.\n\nDel panel de YouTube:\n0:14\n14 seconds\n♪ Here comes the sun ♪\n\nO mejor: el subtítulo .srt o .vtt del vídeo, que trae milisegundos.\n00:00:14,230 --> 00:00:17,500\nHere comes the sun`}
                            />
                            <div className="se-lyrics-footer">
                                <button
                                    className="se-btn-primary"
                                    disabled={!videoId || !rawLyrics || !title || !artist || loading}
                                    onClick={handleParseTranscript}
                                >
                                    {loading ? '⏳ Parseando...' : 'Parsear Lyrics →'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ════════════ STEPS 2-4 ════════════ */}
                {step >= 2 && (
                    <div className="se-editor">

                        {/* ─── LEFT PANE ─── */}
                        <div className="se-left">
                            <div id="sync-yt-player" ref={ytContainer} className="se-player" />

                            {/* Transport */}
                            <div className="se-transport">
                                <div className="se-transport-time">
                                    <span className="se-t-cur">{formatTime(currentTime)}</span>
                                    <span className="se-t-sep">/</span>
                                    <span className="se-t-tot">{formatTime(timeline.total)}</span>
                                </div>
                                <div className="se-transport-speed">
                                    {[0.5, 0.75, 1, 1.25].map(r => (
                                        <button key={r} className={`se-speed-btn ${playbackRate === r ? 'active' : ''}`} onClick={() => changeSpeed(r)}>
                                            {r}x
                                        </button>
                                    ))}
                                </div>
                                <button className={`se-follow-btn ${followMode ? 'active' : ''}`} onClick={() => setFollowMode(f => !f)}>
                                    {followMode ? '🔒' : '🔓'} Follow
                                </button>
                            </div>

                            {/* Step 2 panel */}
                            {step === 2 && (
                                <div className="se-panel">
                                    <div className="se-panel-row">
                                        <span className="se-panel-label">⏱ Offset Global</span>
                                        <span className="se-offset-val">{globalOffset > 0 ? '+' : ''}{globalOffset.toFixed(3)}s</span>
                                    </div>
                                    {/* Estos botones se llamaban igual que los de una línea —+0.5—
                                        y están al lado. Rob pulsó uno creyendo que alargaba un
                                        verso y le movió la canción entera. Ahora lo dicen antes
                                        de que se pulsen, no después. */}
                                    <div className="se-offset-aviso">
                                        ⚠️ Mueve <strong>las {lines.length} líneas a la vez</strong>, no sólo la de abajo.
                                        Para una sola, usa los botones de su fila.
                                    </div>
                                    <div className="se-offset-btns">
                                        {[-1, -0.5, -0.1, 0.1, 0.5, 1].map(v => (
                                            <button key={v} className="se-offset-btn"
                                                    onClick={() => applyGlobalOffset(v)}
                                                    title={`Mover las ${lines.length} líneas ${v > 0 ? '+' : ''}${v}s`}>
                                                {v > 0 ? '+' : ''}{v}s
                                            </button>
                                        ))}
                                    </div>
                                    {globalOffset !== 0 && (
                                        <button className="se-offset-undo" onClick={quitaElDesfase}
                                                title="Devolver las líneas a donde estaban antes de tocar el desfase">
                                            ↩ Quitar el desfase ({globalOffset > 0 ? '+' : ''}{globalOffset.toFixed(1)}s)
                                        </button>
                                    )}
                                    <div className="se-kbd-hint">
                                        <kbd>SPACE</kbd> = Stampar · <kbd>⚡</kbd> = Precisión · <kbd>🎧</kbd> = Preview −3s
                                    </div>
                                    <div className="se-line-counter">
                                        Línea activa: <b>{activeLineIdx + 1}</b> / <b>{lines.length}</b>
                                        {precisionOpen > 0 && (
                                            <span style={{ color: 'var(--cyan)', marginLeft: 8 }}>· ⚡ {precisionOpen} abiertos</span>
                                        )}
                                    </div>
                                    <div className="se-precision-legend">
                                        <div className="se-prec-legend-item">
                                            <span>⚡</span> <span>Panel INICIO + FIN con handles arrastrables</span>
                                        </div>
                                        <div className="se-prec-legend-item">
                                            <span>🟢</span> <span>Handle INICIO de esta línea</span>
                                        </div>
                                        <div className="se-prec-legend-item">
                                            <span>🟠</span> <span>Handle FIN = INICIO de la siguiente (cascade)</span>
                                        </div>
                                        <div className="se-prec-legend-item">
                                            <span>🎧</span> <span>Preview −3s → punto → +3s → auto-pausa</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3 panel */}
                            {step === 3 && (
                                <div className="se-panel">
                                    <div className="se-panel-title">🤖 Auto-Blank por Nivel</div>
                                    <p className="se-panel-hint">Coloreadas por dificultad. Aplica blancos automáticos o haz clic individual.</p>
                                    <div className="se-autoblank-grid">
                                        {Object.entries(DIFF_CONFIG).map(([key, cfg]) => (
                                            <button key={key} className="se-autoblank-card" style={{ '--dc': cfg.color, '--ddk': cfg.darkColor }} onClick={() => autoBlankByDifficulty(key)}>
                                                <span className="se-ab-emoji">{cfg.emoji}</span>
                                                <span className="se-ab-label">{cfg.label}</span>
                                                <span className="se-ab-sub">Máx {cfg.maxPerLine === 99 ? '∞' : cfg.maxPerLine}/línea</span>
                                            </button>
                                        ))}
                                    </div>
                                    <button className="se-btn-ghost small" onClick={clearAllBlanks}>🧹 Limpiar todo</button>
                                    <button className="se-btn-primary small" onClick={generateAllDistractors} disabled={loading || stats.blanked === 0} style={{ marginTop: '8px', width: '100%' }}>
                                        {loading ? '⏳ Generando...' : `🤖 Generar distractores (${stats.blanked - stats.withDistractors} pendientes)`}
                                    </button>
                                    <div className="se-stats-mini">
                                        <div className="se-mini-stat"><span>Palabras</span><b>{stats.total}</b></div>
                                        <div className="se-mini-stat"><span>Blancos</span><b>{stats.blanked}</b></div>
                                        <div className="se-mini-stat"><span>Cobertura</span><b>{stats.coverage}%</b></div>
                                        <div className="se-mini-stat">
                                            <span>Con IA 🤖</span>
                                            <b style={{ color: stats.withDistractors === stats.blanked && stats.blanked > 0 ? '#4ade80' : '#fb923c' }}>
                                                {stats.withDistractors}/{stats.blanked}
                                            </b>
                                        </div>
                                    </div>
                                    <div className="se-legend">
                                        {Object.entries(DIFF_CONFIG).map(([key, cfg]) => (
                                            <div key={key} className="se-legend-item" style={{ '--dc': cfg.color }}>
                                                <div className="se-legend-dot" />
                                                <span>{cfg.label}: {stats.byLevel[key] || 0}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Step 4 panel */}
                            {step === 4 && (
                                <div className="se-panel">
                                    <div className="se-panel-title">🎮 Preview del Juego</div>
                                    <p className="se-panel-hint">Dale Play. Verifica tiempos, blancos y ritmo.</p>
                                    <div className="se-preview-stats">
                                        <div>🎵 {lines.length} líneas</div>
                                        <div>📝 {stats.blanked} blancos</div>
                                        <div>📊 {stats.coverage}% cobertura</div>
                                        <div>🎯 {gameDifficulty}</div>
                                        <div style={{ color: stats.withDistractors === stats.blanked && stats.blanked > 0 ? '#4ade80' : '#fb923c' }}>
                                            🤖 {stats.withDistractors}/{stats.blanked} con IA
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Navigation */}
                            <div className="se-nav-panel">
                                {step === 2 && <button className="se-btn-primary" onClick={() => setStep(3)}>Ir a Blancos →</button>}
                                {step === 3 && <button className="se-btn-primary" onClick={() => setStep(4)}>Preview del Juego →</button>}
                                {step === 4 && (
                                    <button className="se-btn-success" onClick={saveToDB} disabled={loading}>
                                        {loading
                                            ? '⏳ Guardando...'
                                            : (editandoId ? '💾 Guardar cambios' : '🚀 Publicar Canción')}
                                    </button>
                                )}
                                <button className="se-btn-ghost" onClick={() => setStep(s => Math.max(1, s - 1))}>← Paso anterior</button>
                            </div>
                        </div>

                        {/* ─── RIGHT PANE ─── */}
                        <div className="se-right">
                            {/* Timeline */}
                            {timeline.total > 0 && (
                                <div className="se-timeline">
                                    <div className="se-tl-labels">
                                        <span>0:00</span>
                                        <span>{formatTime(timeline.total / 2)}</span>
                                        <span>{formatTime(timeline.total)}</span>
                                    </div>
                                    <div className="se-tl-track">
                                        {timeline.segments.map(seg => seg.pct !== null && (
                                            <div
                                                key={seg.idx}
                                                className={`se-tl-seg ${seg.idx === activeLineIdx ? 'active' : ''} ${seg.blanks > 0 ? 'blanked' : ''} ${openPrecision.has(seg.idx) ? 'precision-open' : ''}`}
                                                style={{ left: `${seg.pct}%` }}
                                                onClick={() => seekToLine(seg.time, seg.idx)}
                                                title={`${formatTime(seg.time)} — ${seg.text?.slice(0, 50)}`}
                                            />
                                        ))}
                                        <div className="se-tl-needle" style={{ left: `${needlePct}%` }}>
                                            <div className="se-tl-needle-line" />
                                            <div className="se-tl-needle-head" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Lines list */}
                            <div className="se-lines-scroll" ref={listRef}>
                                {lines.map((l, lIdx) => (
                                    <div
                                        key={lIdx}
                                        className={`se-line ${lIdx === activeLineIdx ? 'active' : ''} ${l.time === null ? 'unstamped' : ''} ${openPrecision.has(lIdx) ? 'precision-mode' : ''}`}
                                    >
                                        {/* ── STEP 2: tune row + precision panel ── */}
                                        {step === 2 && (
                                            <>
                                                <div className="se-tune-row">
                                                    {/* ── EN LA LISTA SÓLO SE ESCUCHA ───────────
                                                        Rob: «si quiero modificar un aspecto de timeline debería
                                                        ser directamente en precisión. Esos iconos no me sirven
                                                        en el modo normal. Lo que a lo mejor sí podría servirme
                                                        es escuchar en menos 5 segundos».

                                                        Aquí estaban los ±0.5 y ±0.1, repetidos de lo que ya hay
                                                        dentro del panel. Dos sitios para lo mismo es un sitio de
                                                        más donde equivocarse — y éste era el de los errores de
                                                        dedo, porque está siempre a la vista y sin la letra ni la
                                                        regla delante para darse cuenta.

                                                        En su lugar, lo que sí se hace desde la lista: oír. */}
                                                    <div className="se-time-pill" onClick={() => seekToLine(l.time, lIdx)}
                                                         title="Clic = oír este verso entero y parar al acabar">
                                                        {formatTime(l.time)}
                                                    </div>

                                                    {l.frases && l.frases.length > 1 && (
                                                        <button
                                                            className="se-tb partir"
                                                            onClick={() => parteLaLinea(lIdx)}
                                                            title={`YouTube juntó ${l.frases.length} frases bajo este tiempo. `
                                                                + 'Partirlas deja la primera con su tiempo y las demás sin marcar, '
                                                                + 'para que les pongas el suyo oyendo la canción.'}
                                                        >
                                                            ✂ Partir en {l.frases.length}
                                                        </button>
                                                    )}

                                                    <button
                                                        className="se-tb oir"
                                                        onClick={() => previewCut(lIdx, l.time, 'start', 5)}
                                                        disabled={l.time === null}
                                                        title="Oír la entrada: desde 5s antes del corte hasta pasado el corte"
                                                    >
                                                        ⏮ 5s antes
                                                    </button>

                                                    {/* 🎧 Quick preview (INICIO side) */}
                                                    {previewing?.idx === lIdx && previewing?.side === 'start' ? (
                                                        <button className="se-preview-cut-btn playing" onClick={stopPreview}>
                                                            <span className="se-preview-pulse" />⏸
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="se-preview-cut-btn"
                                                            onClick={() => previewCut(lIdx, l.time, 'start')}
                                                            disabled={l.time === null}
                                                            title="Preview INICIO: 3s antes → timestamp → 3s → auto-pausa"
                                                        >
                                                            🎧
                                                        </button>
                                                    )}

                                                    {/* ⚡ Precision toggle */}
                                                    <button
                                                        className={`se-precision-toggle ${openPrecision.has(lIdx) ? 'active' : ''}`}
                                                        onClick={() => togglePrecision(lIdx)}
                                                        title="Panel START/END de precisión"
                                                    >
                                                        ⚡ {openPrecision.has(lIdx) ? 'CERRAR' : 'PRECISIÓN'}
                                                    </button>

                                                    <button className="se-active-btn" onClick={() => setActiveLineIdx(lIdx)}>
                                                        {lIdx === activeLineIdx ? '●' : '○'}
                                                    </button>
                                                </div>

                                                {/* v5.2: Enhanced precision panel with START/END */}
                                                {openPrecision.has(lIdx) && (
                                                    <LinePrecisionPanel
                                                        lineIdx={lIdx}
                                                        line={l}
                                                        prevLine={lIdx > 0 ? lines[lIdx - 1] : null}
                                                        nextLine={lIdx < lines.length - 1 ? lines[lIdx + 1] : null}
                                                        lines={lines}
                                                        duracion={duracionDeLaCancion}
                                                        onAcomoda={acomodaLaLinea}
                                                        previewing={previewing}
                                                        onPreviewStart={previewCut}
                                                        onStopPreview={stopPreview}
                                                        onSetTime={setLineTime}
                                                        playerRef={playerRef}
                                                    />
                                                )}
                                            </>
                                        )}

                                        {/* Steps 3-4: compact time */}
                                        {step >= 3 && (
                                            <div className="se-time-mini" onClick={() => seekToLine(l.time, lIdx)}>
                                                {formatTime(l.time)}
                                            </div>
                                        )}

                                        {/* Line content */}
                                        <div className="se-line-text" onClick={() => step === 2 && seekToLine(l.time, lIdx)}>
                                            {step === 2 && (
                                                <span className={l.time === null ? 'se-unstamped-text' : ''}>{l.text}</span>
                                            )}

                                            {step === 3 && l.words.map((w, wIdx) => (
                                                <span key={wIdx} className="se-word-slot">
                                                    <span
                                                        className={`se-word ${w.isBlank ? 'blank' : ''} ${w.level ? `lv-${w.level}` : 'lv-none'}`}
                                                        style={w.level ? { '--wc': DIFF_CONFIG[w.level]?.color + (w.isBlank ? '' : '55') } : {}}
                                                        onClick={() => toggleBlank(lIdx, wIdx)}
                                                    >
                                                        {w.original}
                                                    </span>
                                                    {w.isBlank && (
                                                        <button
                                                            className={`se-ia-btn ${w.hasDistractors ? 'ready' : ''}`}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setLoading(true);
                                                                generateDistractors(lIdx, wIdx).finally(() => setLoading(false));
                                                            }}
                                                            disabled={loading}
                                                        >
                                                            {w.hasDistractors ? '✓' : '🤖'}
                                                        </button>
                                                    )}
                                                    {' '}
                                                </span>
                                            ))}

                                            {step === 4 && l.words.map((w, wIdx) => (
                                                <span key={wIdx} className="se-word-preview-wrap">
                                                    {w.isBlank
                                                        ? <span className="se-blank-preview" style={{ minWidth: `${Math.max(40, w.clean.length * 9)}px` }}>
                                                            {'_'.repeat(Math.max(4, w.clean.length))}
                                                        </span>
                                                        : w.original}
                                                    {' '}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}